<template>
    <v-container class="form-container">
        <page-header :title="pageTitle" :subtitle="subtitle" :back="listPath">
            <v-chip v-if="!isCreate && !loadingEntity" small :color="isScanned ? 'success' : 'warning'" outlined>
                {{ isScanned ? 'Scan completed' : 'Scan pending' }}
            </v-chip>
            <template v-if="isAdmin && !isCreate">
                <delete-button
                    label="Delete"
                    title="Delete this transaction?"
                    :loading="deleting"
                    @delete-entry="deleteTransaction"
                />
                <v-btn v-if="isShow" color="primary" depressed @click="goEdit">
                    <v-icon left>mdi-pencil-outline</v-icon>Edit
                </v-btn>
            </template>
        </page-header>

        <v-form ref="form" v-model="valid" :readonly="readOnly" @submit.prevent="onSubmit">
            <v-card outlined class="mb-4" :loading="loadingEntity">
                <v-card-title class="section-title">Transaction</v-card-title>
                <v-card-text>
                    <v-row dense>
                        <v-col cols="12" md="6">
                            <entity-search
                                v-model="transaction.partner"
                                :search-fn="searchPartners"
                                item-text="title"
                                :rules="required"
                                :readonly="readOnly"
                                label="Partner *"
                                prepend-icon="mdi-storefront-outline"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <entity-search
                                v-model="transaction.member"
                                :search-fn="searchMembers"
                                :item-text="memberLabel"
                                :rules="required"
                                :readonly="readOnly"
                                label="Member *"
                                hint="Type a name or a card number"
                                prepend-icon="mdi-account-outline"
                            />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-select v-model="transaction.type" :items="types" label="Transaction type" :clearable="!readOnly" outlined />
                        </v-col>
                        <v-col cols="12" md="6">
                            <v-text-field v-model.trim="transaction.description" label="Description" outlined />
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <v-row>
                <v-col v-if="receiptImageUrl" cols="12" md="5">
                    <v-card outlined>
                        <v-card-title class="section-title">
                            Receipt image
                            <v-spacer />
                            <v-btn small text color="primary" :href="receiptImageUrl" target="_blank">
                                <v-icon left small>mdi-open-in-new</v-icon>Open
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <a :href="receiptImageUrl" target="_blank">
                                <v-img :src="receiptImageUrl" contain max-height="560" class="receipt-image" />
                            </a>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" :md="receiptImageUrl ? 7 : 12">
                    <v-card outlined>
                        <v-card-title class="section-title">
                            Receipt
                            <v-spacer />
                            <v-btn v-if="receipt.url" small text color="primary" :href="receipt.url" target="_blank">
                                <v-icon left small>mdi-link-variant</v-icon>Receipt link
                            </v-btn>
                        </v-card-title>
                        <v-card-text>
                            <v-row dense>
                                <v-col cols="12" sm="4">
                                    <v-text-field v-model="receipt.initialValue" label="Initial value" type="number" suffix="€" outlined />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field v-model="receipt.total" label="Total" type="number" suffix="€" outlined />
                                </v-col>
                                <v-col cols="12" sm="4">
                                    <v-text-field v-model="receipt.cleanTotal" label="Clean total" type="number" suffix="€" outlined />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-combobox
                                        v-model="receipt.vat"
                                        :items="vats"
                                        label="VAT number (ΑΦΜ)"
                                        :clearable="!readOnly"
                                        outlined
                                    />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <date-field v-model="receipt.date" label="Receipt date" :readonly="readOnly" />
                                </v-col>
                                <v-col cols="12" sm="6">
                                    <v-text-field v-model.trim="receipt.name" label="Name" outlined />
                                </v-col>
                                <v-col cols="6" sm="3">
                                    <v-text-field v-model.trim="receipt.fhm" label="ΦΗΜ" outlined />
                                </v-col>
                                <v-col cols="6" sm="3">
                                    <v-text-field v-model.trim="receipt.aa" label="Α/Α" outlined />
                                </v-col>
                                <v-col cols="12">
                                    <v-text-field v-model.trim="receipt.url" label="Receipt URL" outlined />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox v-model="receipt.scanCompleted" label="Scan completed" hide-details class="mt-0" />
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <div v-if="!readOnly" class="form-actions">
                <v-btn text @click="goBack">Cancel</v-btn>
                <v-btn color="primary" depressed large type="submit" :loading="saving" :disabled="!valid || loadingEntity">
                    <v-icon left>mdi-content-save-outline</v-icon>{{ isCreate ? 'Create transaction' : 'Save changes' }}
                </v-btn>
            </div>
        </v-form>
    </v-container>
</template>

<script>
    import partnerService from '@/services/partner-service'
    import memberService from '@/services/member-service'
    import transactionService from '@/services/transaction-service'
    import vatService from '@/services/vat-service'
    import paronApi from '@/utils/paron-api'
    import formPage from '@/mixins/form-page'
    import PageHeader from '@/components/PageHeader'
    import DateField from '@/components/DateField'
    import DeleteButton from '@/components/DeleteButton'
    import EntitySearch from '@/components/EntitySearch'
    import { entityId, formatCurrency, formatDate, memberLabel, pick, toInputDate } from '@/utils/format'

    const RECEIPT_FIELDS = ['initialValue', 'total', 'cleanTotal', 'vat', 'name', 'date', 'fhm', 'aa', 'url', 'scanCompleted']

    function emptyReceipt() {
        return { initialValue: null, total: null, cleanTotal: null, vat: null, name: null, date: null, fhm: null, aa: null, url: null, scanCompleted: false }
    }

    export default {
        name: 'TransactionForm',
        components: { PageHeader, DateField, DeleteButton, EntitySearch },
        mixins: [formPage],
        data: () => {
            return {
                entityLabel: 'Transaction',
                transactionId: null,
                deleting: false,
                receipt: emptyReceipt(),
                transaction: {
                    member: null,
                    partner: null,
                    voucher: null,
                    description: '',
                    type: null,
                    image: null,
                    scanCompleted: false
                },
                types: [
                    { text: 'Regular', value: 'regular' },
                    { text: 'Prepaid', value: 'prepaid' },
                    { text: 'B2B', value: 'b2b' }
                ],
                vats: []
            }
        },
        computed: {
            isAdmin() {
                return this.$store.getters.isAdmin
            },
            isScanned() {
                return !!(this.transaction.scanCompleted || this.receipt.scanCompleted)
            },
            receiptImageUrl() {
                return paronApi.imageUrl(this.transaction.image)
            },
            subtitle() {
                if (this.isCreate) {
                    return null
                }
                return [
                    this.transaction.partner ? this.transaction.partner.title : null,
                    this.receipt.date ? formatDate(this.receipt.date) : null,
                    this.receipt.total !== null && this.receipt.total !== '' ? formatCurrency(this.receipt.total) : null
                ].filter(Boolean).join(' · ')
            }
        },
        created() {
            if (!this.readOnly) {
                vatService.getAllVats().then(resp => {
                    this.vats = [...new Set(resp.data.data.map(vat => vat.number).filter(Boolean))]
                })
            }
            if (this.isCreate) {
                return
            }
            this.transactionId = parseInt(this.$route.params.transactionId)
            this.loadingEntity = true
            transactionService.getTransactionById(this.transactionId).then(resp => {
                const transaction = resp.data.data[0]
                this.transaction = { ...this.transaction, ...transaction }
                if (transaction.receipt) {
                    this.receipt = { ...emptyReceipt(), ...transaction.receipt, date: toInputDate(transaction.receipt.date) }
                }
            }).catch(error => {
                this.$notify.error(error, 'Could not load the transaction')
            }).finally(() => {
                this.loadingEntity = false
            })
        },
        methods: {
            memberLabel,
            searchPartners: query => partnerService.searchPartners(query),
            searchMembers: query => memberService.searchMembers(query),
            buildPayload() {
                const receipt = pick(this.receipt, RECEIPT_FIELDS)
                return {
                    member: entityId(this.transaction.member),
                    partner: entityId(this.transaction.partner),
                    // TransactionType clears missing fields, so the linked voucher has to be sent back
                    voucher: entityId(this.transaction.voucher),
                    description: this.transaction.description,
                    type: this.transaction.type,
                    receipt,
                    scanCompleted: !!(receipt.scanCompleted || this.transaction.scanCompleted)
                }
            },
            onSubmit() {
                if (!this.validate()) {
                    return
                }
                this.saving = true
                const payload = this.buildPayload()
                const request = this.isEdit
                    ? transactionService.editTransactionById(this.transactionId, payload)
                    : transactionService.createTransaction(payload)
                request.then(() => {
                    this.$notify.success(this.isCreate ? 'Transaction created' : 'Transaction saved')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not save the transaction')
                }).finally(() => {
                    this.saving = false
                })
            },
            deleteTransaction() {
                this.deleting = true
                transactionService.deleteTransactionById(this.transactionId).then(() => {
                    this.$notify.success('Transaction deleted')
                    this.goBack()
                }).catch(error => {
                    this.$notify.error(error, 'Could not delete the transaction')
                }).finally(() => {
                    this.deleting = false
                })
            }
        }
    }
</script>

<style scoped>
.receipt-image {
    border-radius: 4px;
    background: #fafafa;
}

</style>
